import React, { useContext } from 'react';
import classNames from 'classnames/bind';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { string, bool } from 'prop-types';
import {
  useDataModel,
  params,
  string as stringType,
  shape as shapeType
} from '@thd-nucleus/data-sources';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import styles from './productbar.module.scss';

const cx = classNames.bind(styles);

const ONLINE_EXCLUSIVE = 'Online';
export const ProductInfoBar = ({ itemId, padded, showOnMobile }) => {
  const { configuredItemId } = useLifeCycleEventBus('configurator.stock_product_chosen_event');
  const actualItemId = configuredItemId || itemId;
  const { channel } = useContext(ExperienceContext);
  const theme = useTheme(ProductInfoBar, { itemId, showOnMobile });
  const { data } = useDataModel('product', { variables: { itemId: actualItemId } });
  if (!data?.product) {
    return null;
  }
  const isMobile = channel === 'mobile';
  const { showUpcCode } = theme?.props || {};
  const { product } = data || {};
  const { identifiers, availabilityType } = product || {};
  const {
    modelNumber,
    rentalCategory,
    rentalSubCategory,
    storeSkuNumber,
    specialOrderSku,
    toolRentalSkuNumber,
    upc,
    productType
  } = identifiers || {};
  const { type } = availabilityType || {};
  const isMajorAppliance = productType === 'MAJOR_APPLIANCE';
  const shouldHideStoreSku = (type === ONLINE_EXCLUSIVE && !isMajorAppliance);
  const productInfoBarClasses = classNames([cx('product-info-bar')], {
    [cx('product-info-bar--padded')]: padded && !isMobile,
    [cx('product-info-bar--paddedMobile')]: padded && isMobile,
    [cx('product-info-bar__mobile')]: showOnMobile
  });

  return (
    <div className={productInfoBarClasses}>
      <h2 className={cx('product-info-bar__detail')}>
        Internet #
        {actualItemId}
      </h2>
      {modelNumber
        && (
          <h2 className={cx('product-info-bar__detail')}>
            Model #
            {modelNumber}
          </h2>
        )}
      {upc && showUpcCode
        && (
          <h2 className={cx('product-info-bar__detail')}>
            UPC Code #
            {upc}
          </h2>
        )}
      {storeSkuNumber && !shouldHideStoreSku
        && (
          <h2 className={cx('product-info-bar__detail')}>
            Store SKU #
            {storeSkuNumber}
          </h2>
        )}
      {specialOrderSku
        && (
          <h2 className={cx('product-info-bar__detail')}>
            Store SO SKU #
            {specialOrderSku}
          </h2>
        )}
      {toolRentalSkuNumber
        && (
          <h2 className={cx('product-info-bar__detail')}>
            Rental SKU #
            {toolRentalSkuNumber}
          </h2>
        )}
      {rentalCategory && rentalSubCategory && (
        <>
          <h2 className={cx('product-info-bar__detail')}>
            Rental Category #
            {rentalCategory}
          </h2>
          <h2 className={cx('product-info-bar__detail')}>
            Rental Subcategory #
            {rentalSubCategory}
          </h2>
        </>
      )}
    </div>
  );
};

ProductInfoBar.themeProps = {
  showUpcCode: bool
};

ProductInfoBar.defaultThemeProps = {
  showUpcCode: false
};

ProductInfoBar.displayName = 'ProductInfoBar';

ProductInfoBar.defaultProps = {
  padded: false,
  showOnMobile: false,
};

ProductInfoBar.propTypes = {
  padded: bool,
  itemId: string.isRequired,
  showOnMobile: bool,
};

ProductInfoBar.dataModel = {
  product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    identifiers: shapeType({
      modelNumber: stringType(),
      storeSkuNumber: stringType(),
      specialOrderSku: stringType(),
      toolRentalSkuNumber: stringType(),
      rentalCategory: stringType(),
      rentalSubCategory: stringType(),
      upc: stringType(),
      productType: stringType(),
    }),
    availabilityType: shapeType({
      type: stringType()
    })
  })
};
